import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, User, Phone, Mail, MapPin, Calendar, FileText, Plus, PhoneCall, Home as HomeIcon, ClipboardList, GitBranch, Send, Handshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ExtendedIntakeForm from './ExtendedIntakeForm';
import DecisionTreeWizard from './DecisionTreeWizard';
import PFCReferralForm from './PFCReferralForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseDetail = ({ user }) => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [neighbor, setNeighbor] = useState(null);
  const [notes, setNotes] = useState([]);
  const [previousCases, setPreviousCases] = useState([]);
  const [assistance, setAssistance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [showAssistanceForm, setShowAssistanceForm] = useState(false);
  const [assistanceForm, setAssistanceForm] = useState({
    request_type: '',
    amount: '',
    provider: '',
    account_number_masked: '',
    notes: '',
    recommendation_type: '',
    recommendation_other: ''
  });

  // Structured recommendation options
  const RECOMMENDATION_OPTIONS = [
    { value: 'approve_rent', label: 'Approve assistance for rent', hasAmount: true },
    { value: 'approve_utilities', label: 'Approve assistance for utilities', hasAmount: true },
    { value: 'approve_medication', label: 'Approve assistance for medication', hasAmount: true },
    { value: 'approve_food', label: 'Approve assistance for food/groceries', hasAmount: true },
    { value: 'approve_transportation', label: 'Approve assistance for transportation', hasAmount: true },
    { value: 'refer_pope_francis', label: 'Refer to Pope Francis Center', hasAmount: false },
    { value: 'refer_catholic_charities', label: 'Refer to Catholic Charities', hasAmount: false },
    { value: 'refer_food_pantry', label: 'Refer to Food Pantry', hasAmount: false },
    { value: 'refer_utility_assistance', label: 'Refer to LIHEAP/Utility Assistance Program', hasAmount: false },
    { value: 'refer_housing', label: 'Refer to Housing Assistance Program', hasAmount: false },
    { value: 'deny_outside_boundary', label: 'Deny - Outside parish boundary', hasAmount: false },
    { value: 'deny_insufficient_funds', label: 'Deny - Insufficient conference funds', hasAmount: false },
    { value: 'deny_duplicate', label: 'Deny - Duplicate request', hasAmount: false },
    { value: 'other', label: 'Other (specify)', hasAmount: true }
  ];
  const [editingNeighbor, setEditingNeighbor] = useState(false);
  const [neighborForm, setNeighborForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });
  const [volunteers, setVolunteers] = useState([]);
  const [showHomeVisitForm, setShowHomeVisitForm] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [followupForm, setFollowupForm] = useState({
    followup_type: 'call',
    outcome: '',
    notes: ''
  });
  
  // W-9 state
  const [w9Requests, setW9Requests] = useState([]);
  const [showW9Form, setShowW9Form] = useState(false);
  const [w9Form, setW9Form] = useState({ landlord_name: '', landlord_email: '' });
  const [sendingW9, setSendingW9] = useState(false);
  
  // Boundary referral state
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [referralForm, setReferralForm] = useState({ referral_type: 'chicago_area', additional_notes: '', override_email: '' });
  const [sendingReferral, setSendingReferral] = useState(false);

  // Follow-up outcome options
  const CALL_OUTCOMES = [
    { value: 'reached', label: 'Reached - Spoke with neighbor' },
    { value: 'voicemail', label: 'Left voicemail' },
    { value: 'no_answer', label: 'No answer' },
    { value: 'wrong_number', label: 'Wrong number' },
    { value: 'disconnected', label: 'Number disconnected' }
  ];

  const VISIT_OUTCOMES = [
    { value: 'completed', label: 'Visit completed successfully' },
    { value: 'no_one_home', label: 'No one home' },
    { value: 'rescheduled', label: 'Rescheduled for later' },
    { value: 'unable_to_locate', label: 'Unable to locate address' }
  ];

  // New feature states
  const [showExtendedIntake, setShowExtendedIntake] = useState(false);
  const [showDecisionTree, setShowDecisionTree] = useState(false);
  const [showPFCReferral, setShowPFCReferral] = useState(false);
  const [pfcReferrals, setPFCReferrals] = useState([]);
  const [extendedIntake, setExtendedIntake] = useState(null);
  const [decisionTreeResults, setDecisionTreeResults] = useState([]);
  const [sendingDocRequest, setSendingDocRequest] = useState(false);

  useEffect(() => {
    fetchCaseDetail();
    fetchAssistance();
    fetchVolunteers();
    fetchFollowups();
    fetchExtendedIntake();
    fetchDecisionTreeResults();
    fetchW9Requests();
    fetchPFCReferrals();
  }, [caseId]);

  useEffect(() => {
    if (neighbor) {
      setNeighborForm({
        first_name: neighbor.first_name || '',
        last_name: neighbor.last_name || '',
        phone: neighbor.phone || '',
        email: neighbor.email || '',
        address: neighbor.address || '',
        city: neighbor.city || '',
        state: neighbor.state || '',
        zip_code: neighbor.zip_code || ''
      });
    }
  }, [neighbor]);

  const fetchCaseDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseData(response.data.case);
      setNeighbor(response.data.neighbor);
      setNotes(response.data.notes);
      setPreviousCases(response.data.previous_cases || []);
    } catch (error) {
      console.error('Error fetching case:', error);
      toast.error('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssistance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assistance/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssistance(response.data);
    } catch (error) {
      console.error('Error fetching assistance:', error);
    }
  };

  const fetchVolunteers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/volunteers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVolunteers(response.data);
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    }
  };

  const fetchFollowups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/followups/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowups(response.data);
    } catch (error) {
      console.error('Error fetching followups:', error);
    }
  };

  const fetchExtendedIntake = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/extended-intake/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExtendedIntake(response.data);
    } catch (error) {
      console.error('Error fetching extended intake:', error);
    }
  };

  const fetchDecisionTreeResults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/decision-tree/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDecisionTreeResults(response.data);
    } catch (error) {
      console.error('Error fetching decision tree:', error);
    }
  };

  const fetchPFCReferrals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cases/${caseId}/pfc-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPFCReferrals(response.data || []);
    } catch (error) {
      console.error('Error fetching PFC referrals:', error);
    }
  };

  const handleRequestDocuments = async () => {
    if (!neighbor?.email) {
      toast.error('Neighbor does not have an email address on file');
      return;
    }
    
    setSendingDocRequest(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/request-documents/${caseId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document request email sent');
      fetchCaseDetail(); // Refresh to show the note
    } catch (error) {
      console.error('Error requesting documents:', error);
      toast.error(error.response?.data?.detail || 'Failed to send document request');
    } finally {
      setSendingDocRequest(false);
    }
  };

  const fetchW9Requests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cases/${caseId}/w9`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setW9Requests(response.data);
    } catch (error) {
      console.error('Error fetching W-9 requests:', error);
    }
  };

  const handleSendW9 = async (e) => {
    e.preventDefault();
    if (!w9Form.landlord_name || !w9Form.landlord_email) {
      toast.error('Please provide landlord name and email');
      return;
    }
    setSendingW9(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cases/${caseId}/w9/send`, w9Form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('W-9 request sent to landlord');
      setShowW9Form(false);
      setW9Form({ landlord_name: '', landlord_email: '' });
      fetchW9Requests();
    } catch (error) {
      console.error('Error sending W-9:', error);
      toast.error(error.response?.data?.detail || 'Failed to send W-9 request');
    } finally {
      setSendingW9(false);
    }
  };

  const handleMarkW9Received = async (w9Id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/w9/${w9Id}/mark-received`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('W-9 marked as received');
      fetchW9Requests();
    } catch (error) {
      console.error('Error marking W-9:', error);
      toast.error('Failed to update W-9 status');
    }
  };

  const handleSendReferral = async (e) => {
    e.preventDefault();
    setSendingReferral(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cases/${caseId}/boundary-referral`, referralForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Boundary referral email sent');
      setShowReferralForm(false);
      setReferralForm({ referral_type: 'chicago_area', additional_notes: '', override_email: '' });
      fetchCaseDetail();
    } catch (error) {
      console.error('Error sending referral:', error);
      toast.error(error.response?.data?.detail || 'Failed to send referral email');
    } finally {
      setSendingReferral(false);
    }
  };

  const handleRecordFollowup = async (e) => {
    e.preventDefault();
    if (!followupForm.outcome) {
      toast.error('Please select an outcome');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/followups`, {
        case_id: caseId,
        ...followupForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Follow-up recorded');
      setShowFollowupForm(false);
      setFollowupForm({ followup_type: 'call', outcome: '', notes: '' });
      fetchFollowups();
      fetchCaseDetail();
    } catch (error) {
      console.error('Error recording followup:', error);
      toast.error('Failed to record follow-up');
    }
  };

  const handleClaimCase = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cases/${caseId}/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Case claimed successfully');
      fetchCaseDetail();
    } catch (error) {
      console.error('Error claiming case:', error);
      toast.error(error.response?.data?.detail || 'Failed to claim case');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setAddingNote(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notes`, {
        case_id: caseId,
        content: newNote
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewNote('');
      toast.success('Note added');
      fetchCaseDetail();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/cases/${caseId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchCaseDetail();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleRecordAssistance = async (e) => {
    e.preventDefault();
    
    // Build recommendation string
    let recommendation = '';
    const selectedRec = RECOMMENDATION_OPTIONS.find(r => r.value === assistanceForm.recommendation_type);
    if (selectedRec) {
      if (assistanceForm.recommendation_type === 'other') {
        recommendation = assistanceForm.recommendation_other;
      } else {
        recommendation = selectedRec.label;
        if (selectedRec.hasAmount && assistanceForm.amount) {
          recommendation += ` - $${parseFloat(assistanceForm.amount).toFixed(2)}`;
        }
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assistance`, {
        case_id: caseId,
        request_type: assistanceForm.request_type,
        amount: parseFloat(assistanceForm.amount) || 0,
        provider: assistanceForm.provider,
        account_number_masked: assistanceForm.account_number_masked,
        notes: assistanceForm.notes,
        recommendation: recommendation,
        recommendation_type: assistanceForm.recommendation_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Assistance recorded');
      setShowAssistanceForm(false);
      setAssistanceForm({ 
        request_type: '', 
        amount: '', 
        provider: '', 
        account_number_masked: '', 
        notes: '',
        recommendation_type: '',
        recommendation_other: ''
      });
      fetchCaseDetail();
      fetchAssistance();
    } catch (error) {
      console.error('Error recording assistance:', error);
      toast.error('Failed to record assistance');
    }
  };

  const handleUpdateNeighbor = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/neighbors/${neighbor.id}`, {
        ...neighborForm,
        email: neighborForm.email || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact information updated');
      setEditingNeighbor(false);
      fetchCaseDetail();
    } catch (error) {
      console.error('Error updating neighbor:', error);
      toast.error(error.response?.data?.detail || 'Failed to update information');
    }
  };

  const handleRequestHomeVisit = async (partnerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/cases/${caseId}`, {
        home_visit_needed: true,
        home_visit_partner_id: partnerId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Home visit partner requested');
      setShowHomeVisitForm(false);
      fetchCaseDetail();
    } catch (error) {
      console.error('Error requesting home visit:', error);
      toast.error('Failed to request home visit partner');
    }
  };

  const handleCompleteHomeVisit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/cases/${caseId}`, {
        home_visit_completed: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Home visit marked as completed');
      fetchCaseDetail();
    } catch (error) {
      console.error('Error completing home visit:', error);
      toast.error('Failed to mark home visit complete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#0072BC] border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-300">Loading case details...</p>
        </div>
      </div>
    );
  }

  if (!caseData || !neighbor) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="text-center">
          <p className="text-slate-300">Case not found</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary mt-4">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const canEditCase = user?.role === 'Admin' || user?.role === 'Case Lead' || caseData.assigned_to === user?.id;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
      {/* Header */}
      <header className="backdrop-blur-md bg-slate-900/50 border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">{caseData.case_number}</h1>
              <p className="text-sm text-slate-400">Case Details</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Neighbor Information */}
            <div className="glass-panel p-6" data-testid="neighbor-info">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-[#0072BC]" />
                  Neighbor Information
                </h2>
                {user?.role === 'Admin' && !editingNeighbor && (
                  <Button
                    onClick={() => setEditingNeighbor(true)}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-slate-300 hover:bg-white/10"
                    data-testid="edit-neighbor-btn"
                  >
                    Edit
                  </Button>
                )}
              </div>

              {editingNeighbor ? (
                <form onSubmit={handleUpdateNeighbor} className="space-y-4" data-testid="edit-neighbor-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                      <input
                        type="text"
                        value={neighborForm.first_name}
                        onChange={(e) => setNeighborForm({...neighborForm, first_name: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        data-testid="edit-first-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                      <input
                        type="text"
                        value={neighborForm.last_name}
                        onChange={(e) => setNeighborForm({...neighborForm, last_name: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        data-testid="edit-last-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={neighborForm.phone}
                        onChange={(e) => setNeighborForm({...neighborForm, phone: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        data-testid="edit-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                      <input
                        type="email"
                        value={neighborForm.email}
                        onChange={(e) => setNeighborForm({...neighborForm, email: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        data-testid="edit-email"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-300 mb-2">Address</label>
                      <input
                        type="text"
                        value={neighborForm.address}
                        onChange={(e) => setNeighborForm({...neighborForm, address: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        data-testid="edit-address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">City</label>
                      <input
                        type="text"
                        value={neighborForm.city}
                        onChange={(e) => setNeighborForm({...neighborForm, city: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        data-testid="edit-city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">State</label>
                      <input
                        type="text"
                        value={neighborForm.state}
                        onChange={(e) => setNeighborForm({...neighborForm, state: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        data-testid="edit-state"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">ZIP Code</label>
                      <input
                        type="text"
                        value={neighborForm.zip_code}
                        onChange={(e) => setNeighborForm({...neighborForm, zip_code: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        data-testid="edit-zip"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-[#0072BC] hover:bg-[#005a96]" data-testid="save-neighbor-btn">
                      Save Changes
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/20 text-slate-300 hover:bg-white/10"
                      onClick={() => {
                        setEditingNeighbor(false);
                        // Reset form to original values
                        setNeighborForm({
                          first_name: neighbor.first_name || '',
                          last_name: neighbor.last_name || '',
                          phone: neighbor.phone || '',
                          email: neighbor.email || '',
                          address: neighbor.address || '',
                          city: neighbor.city || '',
                          state: neighbor.state || '',
                          zip_code: neighbor.zip_code || ''
                        });
                      }}
                      data-testid="cancel-edit-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Name</p>
                    <p className="font-medium text-white">{neighbor.first_name} {neighbor.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 flex items-center gap-1">
                      <Phone className="w-4 h-4" /> Phone
                    </p>
                    <p className="font-medium text-white">{neighbor.phone}</p>
                  </div>
                  {neighbor.email && (
                    <div>
                      <p className="text-sm text-slate-400 flex items-center gap-1">
                        <Mail className="w-4 h-4" /> Email
                      </p>
                      <p className="font-medium text-white">{neighbor.email}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-400 flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> Address
                    </p>
                    <p className="font-medium text-white">{neighbor.address}, {neighbor.city}, {neighbor.state} {neighbor.zip_code}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Case Details */}
            <div className="glass-panel p-6" data-testid="case-info">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0072BC]" />
                Request Details
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-400">Request Types</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {caseData.request_types.map(type => (
                      <span key={type} className="badge badge-new">{type}</span>
                    ))}
                  </div>
                </div>
                {caseData.amount_requested && (
                  <div>
                    <p className="text-sm text-slate-400">Amount Requested</p>
                    <p className="text-2xl font-bold text-[#0072BC]">${caseData.amount_requested.toFixed(2)}</p>
                  </div>
                )}
                {caseData.details && (
                  <div>
                    <p className="text-sm text-slate-400">Details</p>
                    <p className="text-slate-200">{caseData.details}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-400">Parish Boundary Check</p>
                  <span className={`badge ${
                    caseData.boundary_check === 'Inside' ? 'badge-assisted' : 'badge-review'
                  }`}>
                    {caseData.boundary_check || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

            {/* Assistance Provided */}
            {assistance.length > 0 && (
              <div className="glass-panel p-6" data-testid="assistance-list">
                <h2 className="text-xl font-semibold text-white mb-4">Assistance & Recommendations</h2>
                <div className="space-y-3">
                  {assistance.map(item => (
                    <div key={item.id} className={`border-l-4 ${
                      item.recommendation_type?.startsWith('approve_') ? 'border-green-500' :
                      item.recommendation_type?.startsWith('refer_') ? 'border-blue-500' :
                      item.recommendation_type?.startsWith('deny_') ? 'border-red-500' :
                      'border-slate-500'
                    } pl-4 py-2 bg-white/5 rounded-r-lg`}>
                      <div className="flex justify-between items-start">
                        <div>
                          {item.recommendation && (
                            <p className="font-semibold text-white mb-1">{item.recommendation}</p>
                          )}
                          <p className="font-medium text-slate-300">{item.request_type}</p>
                          {item.provider && <p className="text-sm text-slate-400">Provider: {item.provider}</p>}
                        </div>
                        {item.amount > 0 && (
                          <p className={`text-lg font-bold ${
                            item.recommendation_type?.startsWith('approve_') ? 'text-green-400' : 'text-slate-400'
                          }`}>${item.amount.toFixed(2)}</p>
                        )}
                      </div>
                      {item.notes && <p className="text-sm text-slate-400 mt-1">{item.notes}</p>}
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(item.provided_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Follow-up Tracking */}
            <div className="glass-panel p-6" data-testid="followup-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <PhoneCall className="w-5 h-5 text-[#0072BC]" />
                  Follow-up Tracking
                </h2>
                {canEditCase && (
                  <Button
                    onClick={() => setShowFollowupForm(!showFollowupForm)}
                    size="sm"
                    className="bg-[#0072BC] hover:bg-[#005a96]"
                    data-testid="add-followup-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Log Follow-up
                  </Button>
                )}
              </div>

              {/* Add Follow-up Form */}
              {showFollowupForm && (
                <form onSubmit={handleRecordFollowup} className="mb-6 p-4 bg-white/5 rounded-lg" data-testid="followup-form">
                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Type *</label>
                      <select
                        value={followupForm.followup_type}
                        onChange={(e) => setFollowupForm({...followupForm, followup_type: e.target.value, outcome: ''})}
                        className="form-select"
                        data-testid="followup-type-select"
                      >
                        <option value="call">Phone Call</option>
                        <option value="home_visit">Home Visit</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Outcome *</label>
                      <select
                        value={followupForm.outcome}
                        onChange={(e) => setFollowupForm({...followupForm, outcome: e.target.value})}
                        className="form-select"
                        required
                        data-testid="followup-outcome-select"
                      >
                        <option value="">Select outcome</option>
                        {(followupForm.followup_type === 'call' ? CALL_OUTCOMES : VISIT_OUTCOMES).map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Notes</label>
                      <textarea
                        value={followupForm.notes}
                        onChange={(e) => setFollowupForm({...followupForm, notes: e.target.value})}
                        className="form-textarea"
                        placeholder="Add details about this follow-up..."
                        rows={3}
                        data-testid="followup-notes-textarea"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-[#0072BC] hover:bg-[#005a96]" data-testid="submit-followup-btn">
                        Save Follow-up
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowFollowupForm(false);
                          setFollowupForm({ followup_type: 'call', outcome: '', notes: '' });
                        }}
                        data-testid="cancel-followup-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </form>
              )}

              {/* Follow-up History */}
              <div className="space-y-3">
                {followups.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No follow-ups recorded yet</p>
                ) : (
                  followups.map(followup => (
                    <div 
                      key={followup.id} 
                      className={`border-l-4 ${
                        followup.followup_type === 'call' ? 'border-blue-500' : 'border-purple-500'
                      } pl-4 py-2`}
                      data-testid={`followup-${followup.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {followup.followup_type === 'call' ? (
                          <PhoneCall className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HomeIcon className="w-4 h-4 text-purple-600" />
                        )}
                        <span className="font-medium text-sm">
                          {followup.followup_type === 'call' ? 'Phone Call' : 'Home Visit'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          followup.outcome === 'reached' || followup.outcome === 'completed' 
                            ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                            : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                        }`}>
                          {(followup.followup_type === 'call' ? CALL_OUTCOMES : VISIT_OUTCOMES)
                            .find(o => o.value === followup.outcome)?.label || followup.outcome}
                        </span>
                      </div>
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-slate-400">{followup.user_name}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(followup.created_at).toLocaleString()}
                        </p>
                      </div>
                      {followup.notes && (
                        <p className="text-sm text-slate-300 mt-1 italic">"{followup.notes}"</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="glass-panel p-6" data-testid="notes-section">
              <h2 className="text-xl font-semibold mb-4">Notes</h2>
              
              {/* Add Note */}
              <div className="mb-6">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  className="mb-2"
                  data-testid="note-textarea"
                />
                <Button
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="bg-[#0072BC] hover:bg-[#005a96]"
                  data-testid="add-note-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </div>

              {/* Notes List */}
              <div className="space-y-4">
                {notes.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No notes yet</p>
                ) : (
                  notes.map(note => (
                    <div key={note.id} className="border-l-4 border-[#0072BC] pl-4 py-2" data-testid={`note-${note.id}`}>
                      <div className="flex justify-between items-start mb-1">
                        <p className="font-medium text-sm">{note.user_name}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                      <p className="text-slate-300">{note.content}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Status Card */}
            <div className="glass-panel p-6" data-testid="status-card">
              <h3 className="font-semibold mb-4">Case Status</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Current Status</p>
                  <span className={`badge ${caseData.status === 'New' ? 'badge-new' : caseData.status.includes('Review') ? 'badge-review' : 'badge-assisted'}`}>
                    {caseData.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-slate-400 mb-1">Created</p>
                  <p className="text-sm font-medium">{new Date(caseData.created_at).toLocaleString()}</p>
                </div>
                {caseData.assigned_to && (
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Assigned To</p>
                    <p className="text-sm font-medium text-white">
                      {volunteers.find(v => v.id === caseData.assigned_to)?.full_name || 'Volunteer'}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-3">
                {!caseData.assigned_to && caseData.status === 'New' && (
                  <Button
                    onClick={handleClaimCase}
                    className="w-full bg-[#0072BC] hover:bg-[#005a96]"
                    data-testid="claim-case-btn"
                  >
                    Claim Case
                  </Button>
                )}

                {canEditCase && (
                  <>
                    <Select onValueChange={handleUpdateStatus} data-testid="status-select">
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Update Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="First Review">First Review</SelectItem>
                        <SelectItem value="Second Review">Second Review</SelectItem>
                        <SelectItem value="Assisted">Assisted</SelectItem>
                        <SelectItem value="Referred">Referred</SelectItem>
                        <SelectItem value="Denied">Denied</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>

                    {(user?.role === 'Admin' || user?.role === 'Case Lead') && caseData.status !== 'Assisted' && (
                      <Button
                        onClick={() => setShowAssistanceForm(!showAssistanceForm)}
                        className="w-full bg-green-600 hover:bg-green-700"
                        data-testid="record-assistance-btn"
                      >
                        Record Assistance
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Neighbor History (previous cases) */}
            {previousCases.length > 0 && (
              <div className="glass-panel p-6" data-testid="neighbor-history-card">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-rose-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                  Returning Neighbor
                  <span className="ml-auto text-xs text-slate-400 font-normal">{previousCases.length} prior case{previousCases.length > 1 ? 's' : ''}</span>
                </h3>
                <div className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-200">This neighbor has reached out to SVdP before. Review the history below before deciding on next steps.</p>
                </div>
                <ul className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="neighbor-history-list">
                  {previousCases.map(pc => {
                    const d = new Date(pc.created_at);
                    const daysAgo = Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
                    let chip;
                    if (daysAgo <= 90) chip = { cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30', label: 'Recent' };
                    else if (daysAgo <= 365) chip = { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30', label: 'Prior' };
                    else chip = { cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30', label: 'Past' };
                    const statusColor = {
                      closed: 'text-emerald-300',
                      in_progress: 'text-blue-300',
                      new: 'text-slate-300',
                      pending_review: 'text-amber-300',
                    }[pc.status] || 'text-slate-300';
                    return (
                      <li
                        key={pc.id}
                        onClick={() => navigate(`/cases/${pc.id}`)}
                        className="p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 cursor-pointer transition-colors"
                        data-testid={`neighbor-history-item-${pc.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-white">{pc.case_number}</span>
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${chip.cls}`}>{chip.label} · {daysAgo}d</span>
                          </div>
                          <span className={`text-xs font-medium ${statusColor}`}>{pc.status}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-xs text-slate-400">
                          <span className="truncate">{(pc.request_types || []).join(', ') || '—'}</span>
                          {pc.amount_requested ? <span className="text-slate-300">${Number(pc.amount_requested).toFixed(2)}</span> : null}
                        </div>
                        {pc.outcome && (
                          <p className="text-xs text-slate-500 mt-1">Outcome: <span className="text-slate-300">{pc.outcome}</span></p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Volunteer Tools Card */}
            {canEditCase && (
              <div className="glass-panel p-6" data-testid="volunteer-tools-card">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#0072BC]" />
                  Volunteer Tools
                </h3>
                
                <div className="space-y-3">
                  {/* Extended Intake Form */}
                  <Button
                    onClick={() => setShowExtendedIntake(true)}
                    variant="outline"
                    className="w-full justify-start"
                    data-testid="extended-intake-btn"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    {extendedIntake ? 'Edit Intake Form' : 'Complete Intake Form'}
                  </Button>
                  {extendedIntake && (
                    <p className="text-xs text-green-600 ml-6">✓ Completed by {extendedIntake.completed_by}</p>
                  )}

                  {/* Decision Tree */}
                  <Button
                    onClick={() => setShowDecisionTree(true)}
                    variant="outline"
                    className="w-full justify-start"
                    data-testid="decision-tree-btn"
                  >
                    <GitBranch className="w-4 h-4 mr-2" />
                    Run Decision Tree
                  </Button>
                  {decisionTreeResults.length > 0 && (
                    <div className="text-xs ml-6">
                      <span className={`${
                        decisionTreeResults[0].final_outcome === 'APPROVE' ? 'text-green-600' :
                        decisionTreeResults[0].final_outcome === 'INELIGIBLE' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        Last result: {decisionTreeResults[0].final_outcome}
                      </span>
                    </div>
                  )}

                  {/* PFC Connect Referral */}
                  <Button
                    onClick={() => setShowPFCReferral(true)}
                    variant="outline"
                    className="w-full justify-start border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                    data-testid="pfc-referral-btn"
                  >
                    <Handshake className="w-4 h-4 mr-2" />
                    Refer to PFC Connect
                  </Button>
                  {pfcReferrals.length > 0 && (
                    <div className="text-xs ml-6">
                      <p className="text-emerald-400">
                        ✓ {pfcReferrals.length} prior referral{pfcReferrals.length > 1 ? 's' : ''} sent
                      </p>
                      <p className="text-slate-500 mt-0.5">
                        Last: {new Date(pfcReferrals[0].sent_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {/* Request Documents */}
                  <Button
                    onClick={handleRequestDocuments}
                    variant="outline"
                    className="w-full justify-start"
                    disabled={sendingDocRequest || !neighbor?.email}
                    data-testid="request-docs-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingDocRequest ? 'Sending...' : 'Request Documents via Email'}
                  </Button>
                  {!neighbor?.email && (
                    <p className="text-xs text-slate-500 ml-6">No email on file</p>
                  )}
                </div>
              </div>
            )}

            {/* W-9 Landlord E-Signature Card */}
            {(user?.role === 'Admin' || user?.role === 'Case Lead' || caseData.assigned_to === user?.id) && (
              <div className="glass-panel p-6" data-testid="w9-card">
                <h3 className="font-semibold mb-4 text-white flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#0072BC]" />
                  W-9 Landlord Form
                </h3>

                {/* W-9 Status */}
                {w9Requests.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {w9Requests.map((w9, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        w9.status === 'completed' 
                          ? 'bg-emerald-500/10 border-emerald-500/30' 
                          : 'bg-amber-500/10 border-amber-500/30'
                      }`} data-testid={`w9-status-${idx}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-white">{w9.landlord_name}</p>
                            <p className="text-xs text-slate-400">{w9.landlord_email}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            w9.status === 'completed'
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {w9.status === 'completed' ? 'Signed' : 'Pending'}
                          </span>
                        </div>
                        {w9.status === 'completed' && w9.signature_name && (
                          <p className="text-xs text-emerald-400 mt-1">
                            {w9.signature_name === 'Received offline' ? 'Received offline (paper copy)' : `Signed by: ${w9.signature_name}`}
                          </p>
                        )}
                        {w9.status === 'pending' && (
                          <button
                            onClick={() => handleMarkW9Received(w9.id)}
                            className="mt-2 text-xs px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/30 rounded-lg transition-colors"
                            data-testid={`w9-mark-received-${idx}`}
                          >
                            Mark as Received (paper copy)
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Send W-9 Form */}
                {!showW9Form ? (
                  <Button
                    onClick={() => setShowW9Form(true)}
                    variant="outline"
                    className="w-full border-[#0072BC]/30 text-[#0072BC] hover:bg-[#0072BC]/10"
                    data-testid="send-w9-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send W-9 to Landlord
                  </Button>
                ) : (
                  <form onSubmit={handleSendW9} className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Landlord Name</label>
                      <input
                        type="text"
                        value={w9Form.landlord_name}
                        onChange={(e) => setW9Form({...w9Form, landlord_name: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-[#0072BC] outline-none"
                        placeholder="Landlord's full name"
                        required
                        data-testid="w9-landlord-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Landlord Email</label>
                      <input
                        type="email"
                        value={w9Form.landlord_email}
                        onChange={(e) => setW9Form({...w9Form, landlord_email: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-[#0072BC] outline-none"
                        placeholder="landlord@email.com"
                        required
                        data-testid="w9-landlord-email-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={sendingW9} size="sm" className="bg-[#0072BC] hover:bg-[#005a96]" data-testid="w9-send-submit-btn">
                        {sendingW9 ? 'Sending...' : 'Send'}
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowW9Form(false)} className="border-white/20 text-slate-300" data-testid="w9-cancel-btn">
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Boundary Referral Card */}
            {(user?.role === 'Admin' || user?.role === 'Case Lead' || caseData.assigned_to === user?.id) && (
              <div className="glass-panel p-6" data-testid="boundary-referral-card">
                <h3 className="font-semibold mb-4 text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-400" />
                  Boundary Referral
                </h3>
                {caseData.boundary_check && caseData.boundary_check !== 'Inside' && (
                  <div className="mb-3 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-xs text-amber-400">This case was flagged as: <span className="font-medium">{caseData.boundary_check === 'Outside_Local' ? 'Outside Parish (Local)' : caseData.boundary_check}</span></p>
                  </div>
                )}

                {!showReferralForm ? (
                  <Button
                    onClick={() => setShowReferralForm(true)}
                    variant="outline"
                    className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                    data-testid="send-referral-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Boundary Referral Email
                  </Button>
                ) : (
                  <form onSubmit={handleSendReferral} className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Neighbor Location</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 p-2.5 bg-white/5 border border-white/10 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                          <input
                            type="radio"
                            name="referral_type"
                            value="chicago_area"
                            checked={referralForm.referral_type === 'chicago_area'}
                            onChange={(e) => setReferralForm({...referralForm, referral_type: e.target.value})}
                            className="text-[#0072BC] focus:ring-[#0072BC]"
                            data-testid="referral-chicago"
                          />
                          <div>
                            <span className="text-sm text-white">Chicago Area</span>
                            <p className="text-xs text-slate-500">Links to SVdP Chicago assistance page</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-2 p-2.5 bg-white/5 border border-white/10 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                          <input
                            type="radio"
                            name="referral_type"
                            value="outside_chicago"
                            checked={referralForm.referral_type === 'outside_chicago'}
                            onChange={(e) => setReferralForm({...referralForm, referral_type: e.target.value})}
                            className="text-[#0072BC] focus:ring-[#0072BC]"
                            data-testid="referral-outside-chicago"
                          />
                          <div>
                            <span className="text-sm text-white">Outside Chicago / Out of State</span>
                            <p className="text-xs text-slate-500">Suggests closest Catholic Church or Catholic Charities</p>
                          </div>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Neighbor Email *</label>
                      <input
                        type="email"
                        value={referralForm.override_email || neighbor?.email || ''}
                        onChange={(e) => setReferralForm({...referralForm, override_email: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-[#0072BC] outline-none"
                        placeholder="neighbor@email.com"
                        required
                        data-testid="referral-email-input"
                      />
                      {!neighbor?.email && <p className="text-xs text-slate-500 mt-1">No email on file — this will be saved to the case</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Additional Notes (optional)</label>
                      <textarea
                        value={referralForm.additional_notes}
                        onChange={(e) => setReferralForm({...referralForm, additional_notes: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-[#0072BC] outline-none resize-none"
                        rows={2}
                        placeholder="Any specific organizations or notes to include..."
                        data-testid="referral-notes"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={sendingReferral} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="referral-send-btn">
                        {sendingReferral ? 'Sending...' : 'Send Referral'}
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowReferralForm(false)} className="border-white/20 text-slate-300" data-testid="referral-cancel-btn">
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Home Visit Card */}
            {caseData.assigned_to === user?.id && (
              <div className="glass-panel p-6" data-testid="home-visit-card">
                <h3 className="font-semibold mb-4">Home Visit</h3>
                
                {!caseData.home_visit_needed ? (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-400">Request a partner for home visit (2 people required)</p>
                    <Button
                      onClick={() => setShowHomeVisitForm(true)}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      data-testid="request-home-visit-btn"
                    >
                      Request Home Visit Partner
                    </Button>
                  </div>
                ) : caseData.home_visit_completed ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-2 border border-emerald-500/30">
                      <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-emerald-400">Home Visit Completed</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-purple-500/10 border border-purple-500/30 p-3 rounded-lg">
                      <p className="text-sm font-medium text-purple-300 mb-1">Partner Requested</p>
                      <p className="text-xs text-purple-400">
                        {volunteers.find(v => v.id === caseData.home_visit_partner_id)?.full_name || 'Volunteer'}
                      </p>
                    </div>
                    <Button
                      onClick={handleCompleteHomeVisit}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="complete-home-visit-btn"
                    >
                      Mark Visit Complete
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Home Visit Partner Selection Dialog */}
            {showHomeVisitForm && (
              <div className="glass-panel p-6" data-testid="home-visit-form">
                <h3 className="font-semibold mb-4">Select Partner for Home Visit</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {volunteers
                    .filter(v => v.id !== user?.id)
                    .map(volunteer => (
                      <button
                        key={volunteer.id}
                        onClick={() => handleRequestHomeVisit(volunteer.id)}
                        className="w-full text-left p-3 border border-white/10 rounded hover:bg-white/5 transition-colors"
                        data-testid={`select-volunteer-${volunteer.id}`}
                      >
                        <p className="font-medium">{volunteer.full_name}</p>
                        <p className="text-xs text-slate-400">{volunteer.role}</p>
                      </button>
                    ))}
                </div>
                <Button
                  onClick={() => setShowHomeVisitForm(false)}
                  variant="outline"
                  className="w-full mt-4"
                  data-testid="cancel-home-visit-btn"
                >
                  Cancel
                </Button>
              </div>
            )}

            {/* Record Assistance Form */}
            {showAssistanceForm && (
              <div className="glass-panel p-6" data-testid="assistance-form">
                <h3 className="font-semibold mb-4">Record Assistance / Recommendation</h3>
                <form onSubmit={handleRecordAssistance} className="space-y-4">
                  {/* Structured Recommendation Dropdown */}
                  <div>
                    <label className="form-label">Recommendation *</label>
                    <select
                      value={assistanceForm.recommendation_type}
                      onChange={(e) => {
                        const value = e.target.value;
                        const selectedRec = RECOMMENDATION_OPTIONS.find(r => r.value === value);
                        setAssistanceForm({
                          ...assistanceForm, 
                          recommendation_type: value,
                          // Auto-set request type based on recommendation
                          request_type: value.startsWith('approve_') ? 
                            value.replace('approve_', '').charAt(0).toUpperCase() + value.replace('approve_', '').slice(1) :
                            assistanceForm.request_type,
                          // Clear amount if recommendation doesn't need it
                          amount: selectedRec?.hasAmount ? assistanceForm.amount : '0'
                        });
                      }}
                      className="form-select"
                      required
                      data-testid="recommendation-type-select"
                    >
                      <option value="">Select recommendation</option>
                      <optgroup label="Approve Assistance">
                        {RECOMMENDATION_OPTIONS.filter(r => r.value.startsWith('approve_')).map(rec => (
                          <option key={rec.value} value={rec.value}>{rec.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Referrals">
                        {RECOMMENDATION_OPTIONS.filter(r => r.value.startsWith('refer_')).map(rec => (
                          <option key={rec.value} value={rec.value}>{rec.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Deny">
                        {RECOMMENDATION_OPTIONS.filter(r => r.value.startsWith('deny_')).map(rec => (
                          <option key={rec.value} value={rec.value}>{rec.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Other">
                        <option value="other">Other (specify)</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Other Recommendation Text */}
                  {assistanceForm.recommendation_type === 'other' && (
                    <div>
                      <label className="form-label">Specify Recommendation *</label>
                      <input
                        type="text"
                        value={assistanceForm.recommendation_other}
                        onChange={(e) => setAssistanceForm({...assistanceForm, recommendation_other: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        placeholder="Enter custom recommendation"
                        data-testid="recommendation-other-input"
                      />
                    </div>
                  )}

                  {/* Assistance Type - shown for approvals */}
                  {assistanceForm.recommendation_type.startsWith('approve_') && (
                    <div>
                      <label className="form-label">Assistance Type</label>
                      <select
                        value={assistanceForm.request_type}
                        onChange={(e) => setAssistanceForm({...assistanceForm, request_type: e.target.value})}
                        className="form-select"
                        required
                        data-testid="assistance-type-select"
                      >
                        <option value="">Select type</option>
                        {caseData.request_types.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                        <option value="Rent">Rent</option>
                        <option value="Utilities">Utilities</option>
                        <option value="Medication">Medication</option>
                        <option value="Food">Food</option>
                        <option value="Transportation">Transportation</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  )}

                  {/* Amount - shown when recommendation needs it */}
                  {RECOMMENDATION_OPTIONS.find(r => r.value === assistanceForm.recommendation_type)?.hasAmount && (
                    <div>
                      <label className="form-label">Amount ($) *</label>
                      <input
                        type="number"
                        value={assistanceForm.amount}
                        onChange={(e) => setAssistanceForm({...assistanceForm, amount: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        required
                        min="0"
                        step="0.01"
                        data-testid="assistance-amount-input"
                      />
                    </div>
                  )}

                  {/* Provider - shown for approvals */}
                  {assistanceForm.recommendation_type.startsWith('approve_') && (
                    <div>
                      <label className="form-label">Provider / Payee</label>
                      <input
                        type="text"
                        value={assistanceForm.provider}
                        onChange={(e) => setAssistanceForm({...assistanceForm, provider: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        placeholder="e.g., ComEd, Landlord name"
                        data-testid="assistance-provider-input"
                      />
                    </div>
                  )}

                  {/* Account Number - shown for approvals */}
                  {assistanceForm.recommendation_type.startsWith('approve_') && (
                    <div>
                      <label className="form-label">Account # (masked)</label>
                      <input
                        type="text"
                        value={assistanceForm.account_number_masked}
                        onChange={(e) => setAssistanceForm({...assistanceForm, account_number_masked: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                        placeholder="****1234"
                        data-testid="assistance-account-input"
                      />
                    </div>
                  )}

                  {/* Notes - always shown */}
                  <div>
                    <label className="form-label">Additional Notes</label>
                    <textarea
                      value={assistanceForm.notes}
                      onChange={(e) => setAssistanceForm({...assistanceForm, notes: e.target.value})}
                      className="form-textarea"
                      placeholder="Any additional details or comments..."
                      data-testid="assistance-notes-textarea"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="submit-assistance-btn"
                    >
                      Submit
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAssistanceForm(false);
                        setAssistanceForm({ 
                          request_type: '', 
                          amount: '', 
                          provider: '', 
                          account_number_masked: '', 
                          notes: '',
                          recommendation_type: '',
                          recommendation_other: ''
                        });
                      }}
                      data-testid="cancel-assistance-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Extended Intake Form Modal */}
      {showExtendedIntake && (
        <ExtendedIntakeForm
          caseId={caseId}
          neighbor={neighbor}
          onClose={() => setShowExtendedIntake(false)}
          onSave={fetchExtendedIntake}
        />
      )}

      {/* Decision Tree Wizard Modal */}
      {showDecisionTree && (
        <DecisionTreeWizard
          caseId={caseId}
          neighbor={neighbor}
          onClose={() => setShowDecisionTree(false)}
          onComplete={fetchDecisionTreeResults}
        />
      )}

      {/* PFC Connect Referral Modal */}
      {showPFCReferral && (
        <PFCReferralForm
          caseId={caseId}
          neighbor={neighbor}
          onClose={() => setShowPFCReferral(false)}
          onSent={() => { fetchPFCReferrals(); fetchCaseDetail(); }}
        />
      )}
    </div>
  );
};

export default CaseDetail;
